/**
 * UsersPage — kasutajate haldus (admin only).
 *
 * - Super-admin (user, silver@rohepaneel.ee): saab luua/kustutada admin- ja operator-kasutajaid
 * - Admin (kristo@semuehitus.ee jt): saab luua/kustutada AINULT operator-kasutajaid
 * - Operator-ile see leht pole ligipääsetav (RoleGuard)
 *
 * Backend endpoindid: /api/auth/users (GET/POST/DELETE) + /api/auth/users/:id/password (PUT)
 *
 * Copyright (c) 2025 Rohepaneel
 */
import { useState, useEffect, useCallback } from 'react';
import { Header } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { UserPlus, KeyRound, Trash2, Shield, ShieldCheck } from 'lucide-react';

const ROLES = [
  { value: 'operator', label: 'Operator (tavaline kasutaja)' },
  { value: 'admin',    label: 'Admin (kasutajate haldus + admin-lehed)' },
];

function RoleBadge({ role, isSuperadmin }) {
  if (isSuperadmin) {
    return <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 3, fontSize: '0.65rem', fontWeight: 600,
      color: '#7c3aed', background: 'rgba(124,58,237,0.08)',
      border: '1px solid rgba(124,58,237,0.3)',
    }}><ShieldCheck size={11} /> Super-admin</span>;
  }
  if (role === 'admin') {
    return <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 3, fontSize: '0.65rem', fontWeight: 600,
      color: '#b45309', background: 'rgba(180,83,9,0.08)',
      border: '1px solid rgba(180,83,9,0.3)',
    }}><Shield size={11} /> Admin</span>;
  }
  return <span style={{
    padding: '2px 8px', borderRadius: 3, fontSize: '0.65rem', fontWeight: 600,
    color: 'var(--text-3)', background: 'var(--surface-2)',
    border: '1px solid var(--border)',
  }}>Operator</span>;
}

export function UsersPage({ wsConnected }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ is_superadmin: false });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Create form
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('operator');
  const [creating, setCreating] = useState(false);

  // Password reset modal
  const [resetTarget, setResetTarget] = useState(null);  // user object
  const [resetPassword, setResetPassword] = useState('');

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch('/api/auth/users');
      if (!r.ok) throw new Error(`${r.status}`);
      const d = await r.json();
      setUsers(d.users || []);
      setMeta(d.me || {});
      setErr(null);
    } catch (e) {
      setErr(`Kasutajate laadimine ebaõnnestus: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUsername || !newPassword) return;
    setCreating(true);
    try {
      const r = await fetch('/api/auth/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: newUsername, password: newPassword, role: newRole }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      setNewUsername(''); setNewPassword(''); setNewRole('operator');
      setErr(null);
      reload();
    } catch (e) {
      setErr(`Kasutaja loomine ebaõnnestus: ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (u) => {
    if (!confirm(`Kustuta kasutaja "${u.username}"?`)) return;
    try {
      const r = await fetch(`/api/auth/users/${u.id}`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      setErr(null);
      reload();
    } catch (e) {
      setErr(`Kustutamine ebaõnnestus: ${e.message}`);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !resetPassword || resetPassword.length < 6) return;
    try {
      const r = await fetch(`/api/auth/users/${resetTarget.id}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: resetPassword }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      setResetTarget(null); setResetPassword('');
      alert(`Parool muudetud: ${resetTarget.username}`);
    } catch (e) {
      setErr(`Parooli lähtestamine ebaõnnestus: ${e.message}`);
    }
  };

  // Mida kasutaja saab teha (oma vaatesse)
  const canCreateAdmin = meta.is_superadmin;
  const availableRoles = canCreateAdmin ? ROLES : ROLES.filter(r => r.value === 'operator');

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg)' }}>
      <Header title="Kasutajad" connected={wsConnected} />
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px', maxWidth: 1100, width: '100%', margin: '0 auto' }}>

        {/* Loomise vorm */}
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 6, padding: 20, marginBottom: 20,
        }}>
          <h2 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserPlus size={16} /> Loo uus kasutaja
          </h2>
          <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 2fr auto', gap: 10, alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-3)', marginBottom: 4 }}>Kasutajanimi (e-post)</label>
              <input type="text" value={newUsername} onChange={(e) => setNewUsername(e.target.value)}
                placeholder="nimi@firma.ee" required
                style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.85rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-3)', marginBottom: 4 }}>Parool</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                placeholder="vähemalt 6 tähemärki" required minLength={6}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.85rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-3)', marginBottom: 4 }}>Roll</label>
              <select value={newRole} onChange={(e) => setNewRole(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.85rem' }}>
                {availableRoles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <button type="submit" disabled={creating}
              style={{
                padding: '6px 16px', borderRadius: 4, fontSize: '0.85rem', fontWeight: 500,
                background: '#18181b', color: 'white', border: 'none', cursor: 'pointer',
                opacity: creating ? 0.6 : 1,
              }}>
              {creating ? 'Loon...' : 'Loo'}
            </button>
          </form>
          {!canCreateAdmin && (
            <div style={{ marginTop: 10, fontSize: '0.72rem', color: 'var(--text-3)' }}>
              Sinul on admin-roll, aga saad luua ainult tavalisi (operator) kasutajaid. Admin-kasutajaid saab luua vaid super-admin.
            </div>
          )}
        </div>

        {err && (
          <div style={{ padding: 10, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 4, color: '#b91c1c', marginBottom: 14, fontSize: '0.8rem' }}>
            {err}
          </div>
        )}

        {/* Kasutajate tabel */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{
            padding: '10px 16px', background: 'var(--surface-2)',
            borderBottom: '1px solid var(--divider)', fontSize: '0.78rem', fontWeight: 600,
          }}>
            Kasutajad ({users.length})
          </div>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>Laadin...</div>
          ) : users.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>Ühtegi kasutajat pole</div>
          ) : (
            users.map((u) => {
              const isSelf = u.username === me?.username;
              // Saa muuta/kustutada kui mitte super-admin (kedagi ei tohi) ja
              //   kui sina superadmin VÕI target on operator
              const canModify = !u.is_superadmin && !isSelf && (meta.is_superadmin || u.role !== 'admin');
              return (
                <div key={u.id} style={{
                  display: 'grid', gridTemplateColumns: '1fr auto auto auto',
                  alignItems: 'center', gap: 12, padding: '10px 16px',
                  borderBottom: '1px solid var(--divider)',
                }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
                    {u.username} {isSelf && <span style={{ fontSize: '0.66rem', color: 'var(--text-3)', marginLeft: 6 }}>(sina)</span>}
                  </div>
                  <RoleBadge role={u.role} isSuperadmin={u.is_superadmin} />
                  <button
                    onClick={() => { setResetTarget(u); setResetPassword(''); }}
                    disabled={!canModify && !isSelf}
                    title={isSelf ? 'Vaheta oma parool — kasuta sidebar nuppu' : 'Lähtesta parool'}
                    style={{
                      padding: '4px 10px', borderRadius: 3, fontSize: '0.72rem',
                      background: 'var(--surface-2)', border: '1px solid var(--border)',
                      color: 'var(--text)', cursor: (canModify && !isSelf) ? 'pointer' : 'not-allowed',
                      opacity: (canModify && !isSelf) ? 1 : 0.4,
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
                    <KeyRound size={12} /> Lähtesta parool
                  </button>
                  <button
                    onClick={() => handleDelete(u)}
                    disabled={!canModify}
                    style={{
                      padding: '4px 10px', borderRadius: 3, fontSize: '0.72rem',
                      background: canModify ? 'rgba(220,38,38,0.08)' : 'var(--surface-2)',
                      border: `1px solid ${canModify ? 'rgba(220,38,38,0.3)' : 'var(--border)'}`,
                      color: canModify ? '#b91c1c' : 'var(--text-4)',
                      cursor: canModify ? 'pointer' : 'not-allowed',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
                    <Trash2 size={12} /> Kustuta
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Parooli lähtestamise modaal */}
        {resetTarget && (
          <div onClick={(e) => e.target === e.currentTarget && setResetTarget(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}>
            <div style={{
              background: 'var(--surface)', borderRadius: 6, padding: 24, width: 420, maxWidth: '90%',
              border: '1px solid var(--border)',
            }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 12 }}>
                Lähtesta parool: {resetTarget.username}
              </h3>
              <input type="password" autoFocus value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)} placeholder="Uus parool (vähemalt 6 tähemärki)"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.88rem', marginBottom: 14 }} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setResetTarget(null)} style={{
                  padding: '6px 14px', borderRadius: 4, fontSize: '0.85rem',
                  background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', cursor: 'pointer',
                }}>Tühista</button>
                <button onClick={handleResetPassword} disabled={resetPassword.length < 6} style={{
                  padding: '6px 14px', borderRadius: 4, fontSize: '0.85rem',
                  background: '#18181b', color: 'white', border: 'none', cursor: 'pointer',
                  opacity: resetPassword.length < 6 ? 0.5 : 1,
                }}>Salvesta</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UsersPage;
